import type { ExplorationView } from "@shared/labs/sourcing";
import { FolderKanban, Loader2 } from "lucide-react";
import { useState } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { rpcClient } from "@/lib/rpc";

/** The project a search flow is running a session for — loaded by index.tsx
 *  from `?project=<id>` and threaded into the flow. */
export interface FlowProject {
  id: string;
  name: string;
  brief: string;
  laModelId: string | null;
  laDisciplineId: string | null;
  laLevel: number | null;
}

function defaultProjectName(brief: string): string {
  const snippet = brief.replace(/\s+/g, " ").trim().slice(0, 80);
  return snippet || "Nova vaga";
}

/**
 * The run↔project affordance on a finished run's footer:
 *  - already linked (a project session) → "Ver projeto";
 *  - standalone → "Criar projeto a partir desta exploração" (one click: the
 *    project inherits the run's brief/query/LA scope, the run becomes its first
 *    session, and its top-N ingest — an owner-only action; a shared-link viewer
 *    gets a 403 toast).
 */
export function RunProjectActions({ view }: { view: ExplorationView }) {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [creating, setCreating] = useState(false);

  if (view.projectId) {
    return (
      <Button
        variant="outline"
        onClick={() => navigate(`/labs/sourcing/projects/${view.projectId}`)}
      >
        <FolderKanban /> Ver projeto
      </Button>
    );
  }

  async function create() {
    if (creating) return;
    setCreating(true);
    try {
      const { projectId } = await rpcClient.labs.sourcingProjectCreate({
        name: defaultProjectName(view.brief),
        foundingExplorationId: view.id,
      });
      navigate(`/labs/sourcing/projects/${projectId}`);
    } catch (err) {
      setCreating(false);
      toast({
        title: "Falha ao criar o projeto",
        description:
          err instanceof Error ? err.message : "Apenas quem rodou a busca pode promovê-la.",
        variant: "destructive",
      });
    }
  }

  return (
    <Button variant="outline" onClick={() => void create()} disabled={creating}>
      {creating ? <Loader2 className="animate-spin" /> : <FolderKanban />}
      Criar projeto desta exploração
    </Button>
  );
}
